import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './LoginPage';
import SalesDetailsPage from './SalesDetailsPage';
import Navbar from './Navbar';
import DashboardPage from './DashboardPage';
import AboutPage from './AboutPage';
import SalesEntryForm from './SalesEntryForm';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import HospitalPortal from './HospitalPortal';
import MainLoginPage from './MainLoginPage';
import Management from './pages/Management';
import ProtectedRoute from './auth/ProtectedRoute';
import { roleConfig } from './config/roles';
import UnauthorizedPage from './pages/UnauthorizedPage';

function AppContent({ isAuthenticated, userRole, username, handleLogin, handleLogout, handleAuthSuccess }) {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/home';

  return (
    <div className="App">
      <ToastContainer position="bottom-right" autoClose={5000} />
      {isAuthenticated && <Navbar isAuthenticated={isAuthenticated} onLogout={handleLogout} username={username} />}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      <div className={`content-container ${!isLoginPage ? 'authenticated' : ''}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage onLogin={handleLogin} onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/home" element={<MainLoginPage />} />
          <Route path="/trf" element={<SalesEntryForm />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected Routes */}
          <Route path="/dashboardPage" element={<ProtectedRoute allowedRoles={roleConfig.dashboard} element={<DashboardPage />} />} />
          <Route path="/sales-details" element={<ProtectedRoute allowedRoles={roleConfig.sales} element={<SalesDetailsPage />} />} />
          <Route path="/hospital-portal" element={<ProtectedRoute allowedRoles={roleConfig.lab} element={<HospitalPortal />} />} />
          <Route path="/management" element={<ProtectedRoute allowedRoles={roleConfig.management} element={<Management />} />} />
          <Route path="/about" element={<ProtectedRoute allowedRoles={roleConfig.about} element={<AboutPage />} />} />

          {/* Default route */}
          <Route path="/" element={isAuthenticated ? <Navigate replace to="/dashboardPage" /> : <Navigate replace to="/home" />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || '');
  const [username, setUsername] = useState(localStorage.getItem('username') || ''); // Add username state

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      setUsername(localStorage.getItem('username') || ''); // Set username on initial load
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const handleLogin = async (username, password) => {
    try {
      // console.log("env", process.env)
      // console.log('API Base URL1:', process.env.REACT_APP_API_BASE_URL);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/login`, {
        username, 
        password,
      });
      
      if (response.data.token) {
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        setUsername(username); // Set username in state
        // console.log("response.data.role",response.data.role)
        localStorage.setItem('token', response.data.token); // Save token for future requests
        localStorage.setItem('role', response.data.role);//save role in local
        localStorage.setItem('username', username); // Save username to localStorage
        localStorage.setItem('regionalCodes', JSON.stringify(response.data.regionalCodes));
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`; // Set default Authorization header
        // console.log("Login successful:", response.data.token);
        // console.log("Login successful:", response.data.message);
      } else {
        // Handle case where login response might not be as expected
        throw new Error('Login failed');
      
      }
    } catch (error) {
      console.error("Login error:", error.response?.data?.message || "An error occurred");
      setIsAuthenticated(false); // Ensure user is not authenticated if login fails
      throw error;
    }
  };

  const handleAuthSuccess = (token, role, user) => {
    setIsAuthenticated(true);
    setUserRole(role);
    setUsername(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token'); // Remove the token from localStorage
    localStorage.removeItem('role');
    localStorage.removeItem('username'); // Remove username from localStorage
    setIsAuthenticated(false); // Update the authenticated state to false
    setUsername(''); // Clear username from state
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('regionalCodes')
  };

  return (
    <Router>
      <AppContent 
        isAuthenticated={isAuthenticated} 
        userRole={userRole} 
        username={username} 
        handleLogin={handleLogin} 
        handleLogout={handleLogout}
        handleAuthSuccess={handleAuthSuccess}
      />
    </Router>
  );
}

export default App;

